import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Youtube, Facebook, Instagram, Twitter, Mail, Globe, PhoneCall, MessagesSquare, AtSign, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];

const META = [
  { match: "reddit", letter: "r/", gold: true },
  { match: "discord", icon: MessagesSquare, gold: true },
  { match: "youtube", icon: Youtube, cls: "bg-red-600" },
  { match: "facebook", icon: Facebook, cls: "bg-blue-600" },
  { match: "website", icon: Globe, cls: "bg-emerald-600" },
  { match: "instagram", icon: Instagram, cls: "bg-pink-600" },
  { match: "threads", icon: AtSign, cls: "bg-slate-800" },
  { match: "mastodon", letter: "m", cls: "bg-violet-600" },
  { match: "bluesky", icon: Globe, cls: "bg-sky-500" },
  { match: "twitter", icon: Twitter, cls: "bg-slate-900" },
  { match: "email", icon: Mail, cls: "bg-rose-600" },
  { match: "whatsapp", icon: PhoneCall, cls: "bg-emerald-600" },
];

function metaFor(platform) {
  const p = platform.toLowerCase();
  if (p === "x" || p.startsWith("x ") || p.includes("twitter")) return { icon: Twitter, cls: "bg-slate-900" };
  return META.find((m) => p.includes(m.match)) || { icon: Globe, cls: "bg-sky-700" };
}

export const SocialStrip = () => {
  const [socials, setSocials] = useState([]);

  useEffect(() => {
    api.get("/socials").then((r) => setSocials(r.data)).catch(() => setSocials([]));
  }, []);

  if (socials.length === 0) return null;

  return (
    <section className="border-t border-sky-100 bg-white py-16 sm:py-20" data-testid="home-social-strip">
      <div className="mx-auto max-w-6xl px-6 sm:px-10">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-sky-700"
        >
          Find Us Everywhere
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ delay: 0.08, duration: 0.8, ease: EASE }}
          className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"
        >
          Search the username on your favourite app.
        </motion.h2>

        <div className="mt-10 flex flex-wrap gap-3">
          {socials.map((s, i) => {
            const meta = metaFor(s.platform);
            const Icon = meta.icon;
            const gold = meta.gold;
            return (
              <motion.a
                key={s.id}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.05, duration: 0.6, ease: EASE }}
                whileHover={{ y: -3 }}
                data-testid={`home-social-${i}`}
                className={`group flex items-center gap-3 rounded-2xl border py-3 pl-3 pr-5 transition-colors duration-200 ${
                  gold
                    ? "border-slate-800 bg-slate-950 hover:border-amber-500/60"
                    : "border-slate-200 bg-slate-50/60 hover:border-sky-300 hover:bg-sky-50"
                }`}
              >
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                    gold
                      ? "border border-amber-500/40 bg-amber-500/10 font-mono text-sm font-bold text-amber-400"
                      : `text-white ${meta.cls}`
                  }`}
                >
                  {meta.letter ? meta.letter : Icon && <Icon size={18} />}
                </span>
                <span className="min-w-0">
                  <span className={`flex items-center gap-1.5 text-sm font-bold ${gold ? "text-amber-400" : "text-slate-900"}`}>
                    {s.platform}
                    <ArrowUpRight size={13} className={`transition-all duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 ${gold ? "text-amber-500/70 group-hover:text-amber-400" : "text-slate-400 group-hover:text-sky-700"}`} />
                  </span>
                  {s.handle && (
                    <span className={`block truncate font-mono text-xs ${gold ? "text-amber-300" : "text-sky-700"}`}>{s.handle}</span>
                  )}
                  {s.badge && !s.handle && (
                    <span className={`block truncate text-xs ${gold ? "text-amber-300/80" : "text-slate-500"}`}>{s.badge}</span>
                  )}
                </span>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
};
